import SpinnerArea from "../../../../components/SpinnerArea";
import useService from "../../../../hooks/useService";

import tasksService from "../../../../services/tasks.service";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { FormikProps, useFormik } from "formik";
import { PeerReviewTaskCommentDto } from "../../../../models/task.model";
import * as Yup from "yup";
import styles from "../Task.module.scss";
import { ChangeEvent, useContext, useState } from "react";
import MainContext from "../../../../context/mainContext";
import { useNavigate } from "react-router";
import { SaveSuccessDialog, CancelDialog } from "../TaskDialogs";
import FileUploader, { FileWrapper } from "../../../../components/FileUploader";
import DocumentView from "../../../../components/DocumentView";

import { TaskTypeProps } from "./TaskTypeProps";
import { DocumentIcon } from "../../../../assets/icons";
import moment from "moment";
import { validateBeforeExecute } from "../../../../misc/utils.functions";
import DocumentDownloadButton from "../../../../components/DocumentDownload/DocumentDownloadButton";

const PeerReview: React.FC<TaskTypeProps> = ({ taskHeader }) => {
  const { t } = useTranslation();
  const { service } = useContext(MainContext);
  const navigate = useNavigate();

  const [details, loadingDetails] = useService(() =>
    tasksService.peerReviewDetails(taskHeader.id)
  );

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelDialogShow, cancelDialogShowSet] = useState(false);

  const [requiredDocumentError, setRequiredDocumentError] = useState<
    boolean | null
  >(null);

  const validationSchema = Yup.object().shape({
    comments: Yup.string().nullable().max(256),
  });

  const [uploadDocument, setUploadDocument] = useState<File | null>();

  const formik = useFormik<PeerReviewTaskCommentDto>({
    initialValues: {
      comments: details?.comments,
      taskId: taskHeader.id,
      anonymousReview: details?.anonymousReview || false,
    },
    onSubmit: () => console.log("submit"),
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnMount: true,
    enableReinitialize: true,
  });

  const handleContinueCondition = (): boolean => {
    if (!uploadDocument && !details?.attachment) {
      setRequiredDocumentError(true);
      return false;
    }

    setRequiredDocumentError(null);
    return true;
  };

  const sendReview = (
    values: PeerReviewTaskCommentDto,
    completeTask: boolean
  ) => {
    service(
      () =>
        tasksService.peerReview({ ...values, completeTask }, uploadDocument),
      {
        callback: () => {
          setSaveSuccess(true);
        },
        loader: setSaving,
      }
    );
  };

  const cancel = (formik: FormikProps<PeerReviewTaskCommentDto>) => {
    if (formik.dirty) cancelDialogShowSet(true);
    else navigate(-1);
  };

  return (
    <SpinnerArea loading={saving || loadingDetails}>
      <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
        <p>
          <Trans 
                i18nKey="peerReviewLegend"
                components={{
                  bold: <strong />,
                }}
                values={{
                  dueDate: moment(taskHeader.deadline).format("MM/DD/YYYY"),
                }}
              />
        </p>
        <div className={styles.peerReviewForm}>
          <div className={styles.instructionsSection}>
            <ul>
              <li>
                <p>{t("peerReviewInstructions.0")}</p>
              </li>
              <li>
                <p>{t("peerReviewInstructions.1")}</p>
              </li>
              <li>
                <p>{t("peerReviewInstructions.2")}</p>
              </li>
            </ul>
            <TechnicalNoteTemplate />
          </div>
          <div className={styles.commentSection}>
            <FormControl fullWidth>
              <FormLabel id="description-label">
                {t("addAShortCommentAboutThePublication")}
              </FormLabel>
              <TextField
                id="comments"
                name="comments"
                placeholder={t("comment")!}
                fullWidth
                rows={3}
                multiline
                value={formik.values.comments || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                helperText={formik.touched.comments && formik.errors.comments}
                error={
                  formik.touched.comments && Boolean(formik.errors.comments)
                }
              />
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  id="anonymousReview"
                  checked={formik.values.anonymousReview}
                  onChange={(event: ChangeEvent<any>, checked: boolean) => {
                    formik.setFieldValue("anonymousReview", checked);
                  }}
                  onBlur={formik.handleBlur}
                />
              }
              label={t("iWantMyReviewToBeAnonymous")}
            />
          </div>
          <div className={styles.uploadComments}>
            <FormLabel>{t("uploadAVersionWithYourComments")}</FormLabel>
            <FileUploader
              title="uploadComments"
              onChange={(file: File | File[] | FileWrapper[]) => {
                setUploadDocument(file as File);
              }}
              allowedExtensions={[".xls", ".xlsx", ".doc", ".docx"]}
              isReadOnly={false}
              errorValidate={requiredDocumentError}
            />
            {details && details.attachment && (
              <DocumentView document={details.attachment!} />
            )}
          </div>
        </div>

        <div className={styles.formActions}>
          <Button className={styles.cancel} onClick={() => cancel(formik)}>
            {t("cancel")}
          </Button>

          <Button
            className={styles.save}
            onClick={() =>
              validateBeforeExecute(formik, () =>
                sendReview(formik.values, false)
              )
            }
          >
            {t("save")}
          </Button>
          <Button
            className={styles.sendComments}
            onClick={() =>
              validateBeforeExecute(
                formik,
                () => sendReview(formik.values, true),
                handleContinueCondition
              )
            }
          >
            {t("sendReview")}
          </Button>
        </div>
      </form>
      <CancelDialog
        open={cancelDialogShow}
        onAccept={() => navigate(-1)}
        onClose={() => cancelDialogShowSet(false)}
      />
      <SaveSuccessDialog
        open={saveSuccess}
        onAccept={() => {}}
        onClose={() => setSaveSuccess(false)}
        title={t("saveSuccess")}
      />
    </SpinnerArea>
  );
};

const TechnicalNoteTemplate: React.FC = () => {
  return (
    <Table className={styles.technicalNoteReviewTemplate}>
      <TableHead>
        <TableRow>
          <TableCell>Technical Note Review Document</TableCell>
          <TableCell className={styles.download}>Document</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell>Technical Note Review Document</TableCell>
          <TableCell className={styles.download}>
            <DocumentDownloadButton
              templateCode="TECHNICAL_REVIEW"
              text="Template"
              type="icon"
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default PeerReview;
